<script setup>
import { ref, reactive, onMounted, watch } from 'vue';
import api from '@/services/api.js';
import logo from "@/assets/img/fondotam-blanco.png";
import Swal from 'sweetalert2';
import { generarActaPDF } from '../../js/ActaTransferencias';

// --- ESTADOS DE LA INTERFAZ ---
const transferenciaExitosa = ref(false);
const datosPDFFinales = ref(null);

const transfer = reactive({
  tipo: 'TR', 
  activo_codigo_input: '',
  activo_id_db: null,
  datos_activo: null,
  emisor: { id: '', nombre: '', cargo: '', seccion: '', regional: '', gerencia: '' },
  reg_receptor: '',
  ger_receptor: '',
  sec_receptor: '', 
  car_receptor: '',
  receptor_id: '',
  num_acta: '', 
  ubicacion_destino: '',
  motivo: '',
});

const listas = reactive({
  regionales: [], gerencias: [], secciones: [], cargos: [], usuarios: [], ubicaciones: [] 
});

// --- LÓGICA DE BÚSQUEDA ---
const buscarDatosActivo = async () => {
  if (!transfer.activo_codigo_input) return;
  try {
    const res = await api.get(`transferencias/buscar-activo/?codigo=${transfer.activo_codigo_input}`);
    transfer.activo_id_db = res.data.activo_id;
    transfer.datos_activo = { descripcion: res.data.descripcion };
    transfer.emisor = res.data.emisor; 

    Swal.fire({ icon: 'success', title: 'Activo Localizado', text: res.data.descripcion, timer: 1500, showConfirmButton: false });
  } catch (error) {
    resetForm(false);
  }
};

// --- WATCHERS JERÁRQUICOS CON ALERTAS (SISTEMA PREVENTIVO) ---

watch(() => transfer.reg_receptor, async (val) => {
  transfer.ger_receptor = ''; 
  transfer.sec_receptor = '';
  transfer.car_receptor = '';
  transfer.receptor_id = '';
  listas.gerencias = []; listas.secciones = []; listas.cargos = []; listas.usuarios = [];

  if (val) {
    try {
      const res = await api.get(`transferencias/gerencias/?regional=${val}`);
      listas.gerencias = res.data;
      
      if (listas.gerencias.length === 0) {
        Swal.fire('Regional sin personal', 'No hay gerencias o funcionarios registrados en esta Regional.', 'warning');
      }
    } catch (e) { console.error(e); }
  }
});

watch(() => transfer.ger_receptor, async (val) => {
  transfer.sec_receptor = '';
  transfer.car_receptor = '';
  transfer.receptor_id = '';
  listas.secciones = []; listas.cargos = []; listas.usuarios = [];

  if (val) {
    try {
      const res = await api.get(`transferencias/secciones/?gerencia=${val}`);
      listas.secciones = res.data;
      
      if (listas.secciones.length === 0) {
        Swal.fire({
          title: 'Gerencia Vacía',
          text: 'No hay secciones o personal registrado bajo esta Gerencia.',
          icon: 'info',
          confirmButtonColor: '#003366'
        });
      }
    } catch (e) { console.error(e); }
  }
});

watch(() => transfer.sec_receptor, async (val) => {
  transfer.car_receptor = '';
  transfer.receptor_id = '';
  listas.cargos = []; listas.usuarios = [];

  if (val) {
    try {
      const res = await api.get(`transferencias/cargos/?seccion=${val}`);
      listas.cargos = res.data;

      if (listas.cargos.length === 0) {
        Swal.fire({
          title: 'Sección Vacía',
          text: 'No hay cargos operativos registrados en esta Sección.',
          icon: 'info',
          confirmButtonColor: '#003366'
        });
      }
    } catch (e) { console.error(e); }
  }
});

watch(() => transfer.car_receptor, async (val) => {
  transfer.receptor_id = '';
  listas.usuarios = [];

  if (val) {
    try {
      const res = await api.get(`transferencias/usuarios/?cargo=${val}`);
      // Filtramos para que el emisor no se transfiera el activo a sí mismo
      listas.usuarios = res.data.filter(u => u.id !== transfer.emisor.id);
      
      if (listas.usuarios.length === 0) {
        Swal.fire({
          title: 'Cargo Vacante (o Auto-Transferencia)',
          text: 'No hay otro funcionario disponible en este cargo para recibir el activo.',
          icon: 'info',
          toast: true,
          position: 'top-end',
          timer: 4500,
          showConfirmButton: false
        });
      }
    } catch (e) { console.error(e); }
  }
});

const resetForm = (full) => {
  if (full) transfer.activo_codigo_input = '';
  transfer.datos_activo = null;
  transfer.emisor = { id: '', nombre: '', cargo: '', seccion: '' }; 
  transfer.receptor_id = '';
  transfer.reg_receptor = '';
  transfer.ger_receptor = '';
  transfer.sec_receptor = ''; 
  transfer.car_receptor = '';
  transfer.ubicacion_destino = '';
  transfer.num_acta = ''; 
};

// --- FUNCIÓN SEPARADA PARA IMPRIMIR ---
const imprimirPDF = () => {
  try {
    if (datosPDFFinales.value) {
      generarActaPDF(datosPDFFinales.value);
    }
  } catch (error) {
    console.error("Error al generar PDF:", error);
    Swal.fire('Error del Documento', 'Hubo un problema al generar el PDF. Revise la consola.', 'error');
  }
};

// --- FUNCIÓN SEPARADA PARA NUEVA OPERACIÓN ---
const nuevaTransferencia = () => {
  transferenciaExitosa.value = false;
  datosPDFFinales.value = null;
  resetForm(true);
};

// --- LÓGICA DE ENVÍO ---
const enviarTransferencia = async () => {
  if (!transfer.num_acta) return Swal.fire('Dato Requerido', 'Debe ingresar el número de acta física manualmente.', 'warning');
  if (!transfer.receptor_id || !transfer.ubicacion_destino) return Swal.fire('Incompleto', 'Faltan datos del receptor o ubicación', 'error');

  const payload = {
    activo: transfer.activo_id_db,
    emisor: transfer.emisor.id,
    receptor: transfer.receptor_id,
    reg_receptor: transfer.reg_receptor,
    ger_receptor: transfer.ger_receptor,
    sec_receptor: transfer.sec_receptor, 
    car_receptor: transfer.car_receptor,
    ubicacion_origen: transfer.ubicacion_origen,
    ubicacion_destino: transfer.ubicacion_destino,
    motivo: transfer.motivo,
    num_acta: transfer.num_acta,
    tipo: transfer.tipo 
  };

  try {
    await api.post('/transferencias/', payload);
  } catch (error) {
    return;
  }

  const receptorData = listas.usuarios.find(u => u.id === transfer.receptor_id);
  const cargoReceptorData = listas.cargos.find(c => c.id === transfer.car_receptor);
  const ubicacionData = listas.ubicaciones.find(u => u.id === transfer.ubicacion_destino);

  datosPDFFinales.value = {
    tipo: transfer.tipo,
    num_acta: transfer.num_acta.toUpperCase(),
    fecha: new Date().toLocaleDateString('es-ES'),
    motivo: transfer.motivo || 'Cambio de Custodio Legal',
    
    emisor_nombre: transfer.emisor?.nombre || transfer.emisor?.username || '---',
    emisor_cargo: transfer.emisor?.cargo || '---',
    
    receptor_nombre: receptorData ? receptorData.nombre_completo : '---',
    receptor_cargo: cargoReceptorData ? cargoReceptorData.nombre : '---',
    
    activo_codigo: transfer.activo_codigo_input.toUpperCase(),
    activo_desc: transfer.datos_activo?.descripcion || '---',
    ubicacion_destino: ubicacionData ? ubicacionData.nombre_sala : '---'
  };

  transferenciaExitosa.value = true;

  const result = await Swal.fire({
    title: '¡Operación Exitosa!',
    text: "El activo fue transferido en el sistema. ¿Desea generar el acta en PDF ahora?",
    icon: 'success',
    showCancelButton: true,
    confirmButtonText: 'Sí, Generar PDF',
    cancelButtonText: 'Ahora no',
    confirmButtonColor: '#003366'
  });

  if (result.isConfirmed) {
    imprimirPDF();
  }
};

onMounted(async () => {
  const token = localStorage.getItem('access');
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    try {
      const [resReg, resUbi] = await Promise.all([
        api.get('/transferencias/regionales/'),
        api.get('/transferencias/listar_ubicaciones/')
      ]);
      listas.regionales = resReg.data;
      listas.ubicaciones = resUbi.data;
    } catch (err) { console.error(err); }
  }
});
</script>
<template>
  <div class="container-fluid py-4 app-bg-transition">
    <div class="card card-custom shadow-lg border-0">
      <div class="header-tamep d-flex justify-content-between align-items-center px-4 py-3 border-radius-top">
        <div class="d-flex align-items-center">
          <div class="logo-container-left me-3"><img :src="logo" class="logo-img"></div>
          <div class="border-start ps-3 border-white opacity-8">
            <h6 class="mb-0 text-white text-uppercase font-weight-bolder">Movimiento de Activos</h6>
            <small class="text-white opacity-7">Gestión de Custodia Legal</small>
          </div>
        </div>
      </div>

      <div v-if="transferenciaExitosa" class="card-body p-5 text-center">
        <i class="fas fa-check-circle text-success mb-3" style="font-size: 5rem;"></i>
        <h3 class="text-azul-tamep text-title-custom font-weight-bolder">Transferencia Procesada</h3>
        <p class="text-custom-muted">El activo <strong class="text-custom">{{ datosPDFFinales?.activo_codigo }}</strong> ha sido transferido correctamente bajo el Acta Nro: <strong class="text-custom">{{ datosPDFFinales?.num_acta }}</strong>.</p>
        
        <div class="d-flex justify-content-center gap-3 mt-5">
          <button @click="imprimirPDF" class="btn btn-outline-dark btn-custom-outline btn-lg mb-0 px-4">
            <i class="fas fa-print me-2"></i> IMPRIMIR ACTA PDF
          </button>
          <button @click="nuevaTransferencia" class="btn btn-azul-tamep btn-lg mb-0 px-4">
            <i class="fas fa-plus me-2"></i> NUEVA OPERACIÓN
          </button>
        </div>
      </div>

      <div v-else class="card-body p-4">
        <div class="row mb-4">
          <div class="col-md-5">
            <label class="label-custom">Tipo de Operación</label>
            <div class="btn-group w-100 shadow-none">
              <button @click="transfer.tipo = 'TR'" :class="['btn btn-sm mb-0', transfer.tipo === 'TR' ? 'btn-azul-tamep' : 'btn-outline-secondary btn-custom-outline']">
                <i class="fas fa-exchange-alt me-2"></i>Transferencia
              </button>
              <button @click="transfer.tipo = 'DE'" :class="['btn btn-sm mb-0', transfer.tipo === 'DE' ? 'btn-danger' : 'btn-outline-secondary btn-custom-outline']">
                <i class="fas fa-undo-alt me-2"></i>Devolución
              </button>
            </div>
          </div>
        </div>

        <div class="section-title mb-3 d-flex align-items-center">
          <div class="step-number me-2">1</div>
          <h6 class="font-weight-bolder mb-0 text-title-custom">Identificación del Bien</h6>
        </div>

        <div class="row mb-4 bg-section-custom p-3 border-radius-lg border-custom-divider" style="border: 1px solid #e9ecef;">
          <div class="col-md-4">
            <label class="label-custom">Código del Equipo</label>
            <div class="input-group">
              <input type="text" class="form-control input-custom border-2" v-model="transfer.activo_codigo_input" @keyup.enter="buscarDatosActivo">
              <button @click="buscarDatosActivo" class="btn btn-azul-tamep mb-0"><i class="fas fa-search"></i></button>
            </div>
          </div>
          <div class="col-md-8">
            <label class="label-custom">Descripción Técnica</label>
            <div class="p-2 input-custom border border-radius-md text-sm border-custom-divider">
              {{ transfer.datos_activo?.descripcion || 'Esperando selección...' }}
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-5 border-end border-custom-divider">
            <div class="section-title mb-3 d-flex align-items-center">
              <div class="step-icon me-2"><i class="fas fa-arrow-up text-danger"></i></div>
              <h6 class="font-weight-bolder mb-0 text-title-custom">Emisor (Entrega)</h6>
            </div>
            <div class="card card-body border border-custom-divider shadow-none bg-section-custom p-3">
              <p class="font-weight-bold text-custom mb-1">{{ transfer.emisor?.nombre || '---' }}</p>
              <p class="text-xs text-custom-muted mb-0">{{ transfer.emisor?.cargo || '---' }}</p>
              <p class="text-xxs text-info mb-0 mt-1" v-if="transfer.emisor?.seccion">{{ transfer.emisor?.seccion }}</p>
            </div>
          </div>

          <div class="col-md-7 ps-md-4">
            <div class="section-title mb-3 d-flex align-items-center">
              <div class="step-icon me-2">
                  <i :class="transfer.tipo === 'TR' ? 'fas fa-arrow-down text-success' : 'fas fa-warehouse text-warning'"></i>
              </div>
              <h6 class="font-weight-bolder mb-0 text-title-custom">
                  {{ transfer.tipo === 'TR' ? 'Receptor (Nuevo Custodio)' : 'Responsable Recepción Almacén' }}
              </h6>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="label-custom">Regional</label>
                <select class="form-select-custom select-custom" v-model="transfer.reg_receptor">
                  <option value="">Seleccione...</option>
                  <option v-for="r in listas.regionales" :key="r.id" :value="r.id">{{ r.nombre }}</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="label-custom">Gerencia</label>
                <select class="form-select-custom select-custom" v-model="transfer.ger_receptor" :disabled="!transfer.reg_receptor">
                  <option value="">Seleccione...</option>
                  <option v-for="g in listas.gerencias" :key="g.id" :value="g.id">{{ g.nombre }}</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="label-custom text-info">Sección / Área</label>
                <select class="form-select-custom select-custom border-info-light" v-model="transfer.sec_receptor" :disabled="!transfer.ger_receptor">
                  <option value="">Seleccione...</option>
                  <option v-for="s in listas.secciones" :key="s.id" :value="s.id">{{ s.nombre }}</option>
                </select>
              </div>
              <div class="col-md-6 mb-3">
                <label class="label-custom">Cargo</label>
                <select class="form-select-custom select-custom" v-model="transfer.car_receptor" :disabled="!transfer.sec_receptor">
                  <option value="">Seleccione...</option>
                  <option v-for="c in listas.cargos" :key="c.id" :value="c.id">{{ c.nombre }}</option>
                </select>
              </div>
              <div class="col-md-12 mb-3">
                <label class="label-custom">Funcionario</label>
                <select class="form-select-custom select-custom border-primary-light" v-model="transfer.receptor_id" :disabled="!transfer.car_receptor">
                  <option value="">Seleccione funcionario...</option>
                  <option v-for="u in listas.usuarios" :key="u.id" :value="u.id">{{ u.nombre_completo }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-4 pt-3 border-top border-custom-divider bg-section-custom border-radius-lg p-3">
          <div class="col-md-12 mb-3">
            <label class="label-custom text-primary">Número de Acta Física (Ingreso Manual)</label>
            <div class="input-group">
              <span class="input-group-text bg-input-addon border-2 border-custom-divider"><i class="fas fa-file-signature text-custom"></i></span>
              <input v-model="transfer.num_acta" type="text" class="form-control border-2 ps-2 font-weight-bold input-custom border-custom-divider" 
                placeholder="Ej: TR-2026-0001" style="text-transform: uppercase;">
            </div>
          </div>
          <div class="col-md-4">
            <label class="label-custom">Ubicación Destino</label>
            <select class="form-select-custom select-custom" v-model="transfer.ubicacion_destino">
              <option v-for="u in listas.ubicaciones" :key="u.id" :value="u.id">{{ u.nombre_sala }}</option>
            </select>
          </div>
          <div class="col-md-8">
            <label class="label-custom">Motivo / Justificación Jurídica</label>
            <textarea class="form-control input-custom border-custom-divider" v-model="transfer.motivo" rows="2"></textarea>
          </div>
        </div>

        <div class="text-center mt-5">
          <button @click="enviarTransferencia" class="btn-tamep-primary">
            <i class="fas fa-check-circle me-2"></i> PROCESAR MOVIMIENTO LEGAL
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* Variables Claras y Transiciones */
.app-bg-transition { transition: background-color 0.3s ease; }
.card-custom { background-color: #ffffff; transition: background-color 0.3s ease, box-shadow 0.3s ease; }
.text-custom { color: #333; transition: color 0.3s ease; }
.text-title-custom { color: #344767; }
.text-custom-muted { color: #6c757d; }
.border-custom-divider { border-color: #e9ecef !important; transition: border-color 0.3s ease; }
.bg-section-custom { background-color: #f8f9fa; transition: background-color 0.3s ease; }
.input-custom, .select-custom { background-color: #ffffff; color: #495057; border-color: #e9ecef; transition: all 0.3s ease; }
.bg-input-addon { background-color: #ffffff; border-color: #e9ecef; }
.btn-custom-outline { color: #6c757d; border-color: #6c757d; }

/* Estilos TAMEP Generales */
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.border-radius-top { border-top-left-radius: 12px; border-top-right-radius: 12px; }
.logo-container-left { background: white; padding: 5px 12px; border-radius: 8px; }
.logo-img { max-height: 35px; }
.text-azul-tamep { color: #003366; }
.btn-azul-tamep { background-color: #003366; color: white; border: none; transition: 0.3s; }
.btn-azul-tamep:hover { background-color: #001d3d; }
.label-custom { font-size: 0.65rem; font-weight: 800; text-transform: uppercase; color: #8392ab; margin-bottom: 4px; display: block; }
.form-select-custom { display: block; width: 100%; padding: 0.5rem; font-size: 0.85rem; border: 1px solid #d2d6da; border-radius: 0.5rem; }
.form-select-custom:focus { border-color: #003366; outline: none; box-shadow: 0 0 0 2px rgba(0,51,102,0.1); }
.step-number { background: #003366; color: white; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: bold; }
.btn-tamep-primary { background: #003366; color: white; border: none; padding: 14px 50px; border-radius: 12px; font-weight: 700; text-transform: uppercase; transition: 0.3s; }
.btn-tamep-primary:hover { background: #004a94; transform: translateY(-3px); box-shadow: 0 4px 6px rgba(0,0,0,0.1); }
.border-info-light { border-color: #17a2b880 !important; }
.border-primary-light { border-color: #00336680 !important; }
</style>

<style>
/* =========================================
   SOPORTE PARA MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom, .dark-mode .card-custom { background-color: #1e1e1e !important; box-shadow: 0 4px 15px rgba(0,0,0,0.5) !important; border: 1px solid #2d2d2d !important; }
.dark-version .text-custom, .dark-mode .text-custom { color: #e0e0e0 !important; }
.dark-version .text-title-custom, .dark-mode .text-title-custom { color: #ffffff !important; }
.dark-version .text-custom-muted, .dark-mode .text-custom-muted { color: #aaaaaa !important; }
.dark-version .border-custom-divider, .dark-mode .border-custom-divider { border-color: #333333 !important; }
.dark-version .bg-section-custom, .dark-mode .bg-section-custom { background-color: #2c2c2c !important; border: 1px solid #333 !important; }

/* Formularios Oscuros */
.dark-version .input-custom,
.dark-version .select-custom,
.dark-mode .input-custom,
.dark-mode .select-custom { 
  background-color: #2a2a2a !important; 
  color: #e0e0e0 !important; 
  border-color: #444444 !important; 
}
.dark-version .input-custom:focus,
.dark-version .select-custom:focus,
.dark-mode .input-custom:focus,
.dark-mode .select-custom:focus { 
  border-color: #4dabf7 !important; 
  box-shadow: 0 0 0 2px rgba(77, 171, 247, 0.2) !important; 
}
.dark-version .input-custom::placeholder, .dark-mode .input-custom::placeholder { color: #888 !important; }
.dark-version .bg-input-addon, .dark-mode .bg-input-addon { background-color: #333 !important; border-color: #444 !important; }

/* Botones Outline Oscuros */
.dark-version .btn-custom-outline, .dark-mode .btn-custom-outline { color: #e0e0e0 !important; border-color: #555 !important; }
.dark-version .btn-custom-outline:hover, .dark-mode .btn-custom-outline:hover { background-color: #333 !important; color: #fff !important; border-color: #777 !important; }

/* Selectores deshabilitados en modo oscuro */
.dark-version .select-custom:disabled,
.dark-version .input-custom:disabled,
.dark-mode .select-custom:disabled,
.dark-mode .input-custom:disabled {
  background-color: #1a1a1a !important;
  color: #666 !important;
  border-color: #333 !important;
  opacity: 1;
}

/* Color azul TAMEP en títulos claros cambia a un azul más legible en fondos oscuros si se requiere, pero el blanco queda bien */
.dark-version .text-azul-tamep.text-title-custom, .dark-mode .text-azul-tamep.text-title-custom { color: #4dabf7 !important; }
</style>